import React from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  IconButton,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * NotificationItem Component
 * Individual notification item in the list
 */
const NotificationItem = ({ notification, onMarkAsRead, formatTime, onCallNotificationClick }) => {
  const isRead = notification.is_read || notification.read_at;
  const notificationType = notification.type || notification.notification_type || 'info';

  // Check if this is a call notification
  const isCallNotification = () => {
    const title = (notification.title || '').toLowerCase();
    const message = (notification.message || '').toLowerCase();
    const metadata = notification.metadata || {};
    
    // Check title for call-related keywords
    const hasCallKeywords = title.includes('incoming') && 
                           (title.includes('call') || title.includes('video'));
    
    // Check message for call-related keywords
    const messageHasCall = message.includes('calling') || message.includes('call');
    
    // Check metadata for call indicators
    const hasCallMetadata = metadata.actionUrl?.includes('/video-call') ||
                           metadata.roomId ||
                           metadata.appointmentId ||
                           metadata.callType;
    
    return hasCallKeywords || (messageHasCall && hasCallMetadata);
  };

  // Get icon based on type
  const getIcon = () => {
    switch (notificationType) {
      case 'success':
        return <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'warning':
        return <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      default:
        return <InfoIcon sx={{ color: 'info.main', fontSize: 20 }} />;
    }
  };

  // Get color based on type
  const getColor = () => {
    switch (notificationType) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  const handleClick = () => {
    // If this is a call notification, trigger the call modal
    if (isCallNotification() && onCallNotificationClick) {
      onCallNotificationClick(notification);
      return;
    }
    
    // Otherwise, mark as read
    if (!isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  const title = notification.title || notification.message?.substring(0, 50) || 'Notification';
  const message = notification.message || notification.body || '';
  const timestamp = notification.created_at || notification.timestamp || notification.createdAt;

  return (
    <ListItem
      disablePadding
      sx={{
        bgcolor: isRead ? 'transparent' : 'action.hover',
        '&:hover': {
          bgcolor: 'action.selected',
        },
        transition: 'background-color 0.2s',
      }}
    >
      <ListItemButton
        onClick={handleClick}
        sx={{
          py: 1.5,
          px: 2,
        }}
      >
        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
          {getIcon()}
        </Box>

        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: isRead ? 400 : 600,
                  flex: 1,
                }}
              >
                {title}
              </Typography>
              {!isRead && (
                <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
              )}
            </Box>
          }
          secondary={
            <Box>
              {message && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {message}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                {timestamp && (
                  <Typography variant="caption" color="text.secondary">
                    {formatTime(timestamp)}
                  </Typography>
                )}
                {notificationType !== 'info' && (
                  <Chip
                    label={notificationType}
                    size="small"
                    color={getColor()}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            </Box>
          }
        />

        {!isRead && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (onMarkAsRead) {
                onMarkAsRead(notification.id);
              }
            }}
            sx={{ ml: 1 }}
          >
            <CheckCircleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          </IconButton>
        )}
      </ListItemButton>
    </ListItem>
  );
};

export default NotificationItem;

